import { useCallback, useEffect, useState } from "react";

export const subjects = [
    {
        id: "whats-new",
        label: "What's new",
    },
    {
        id: "none",
        label: "None",
    },
    {
        id: "dashboards",
        label: "Dashboards",
    },
    {
        id: "reports",
        label: "Reports",
    },
    {
        id: "devices",
        label: "Devices",
    },
    {
        id: "software-release-notes",
        label: "CORE Software Release Notes",
    },
    {
        id: "sites-and-organisations",
        label: "Sites and organisations",
    },
    {
        id: "manage-customers",
        label: "Manage customers",
    },
    {
        id: "settings",
        label: "Settings",
    },
] as const;

export type Post = {
    title: string;
    file: string;
    slug: string;
    staffOnly: boolean;
    isPublished: boolean;
    ranking?: number;
    subject?: (typeof subjects)[number]["id"];
    coreVersion?: string;
    body: string;
};

export const useHelpPostManifest = ({ enabled }: { enabled?: boolean }) => {
    const [postManifest, setPostManifest] = useState<Omit<Post, "body">[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const [isError, setIsError] = useState<boolean>(false);

    const fetchPosts = useCallback(async () => {
        setIsLoading(true);
        setIsError(false);
        try {
            await fetch("/admin/file-manifest.json")
                .then((response) => response.json())
                .then((data) => setPostManifest(data));
        } catch {
            setIsError(true);
        } finally {
            setIsLoading(false);
            setIsFetched(true);
        }
    }, []);

    useEffect(() => {
        if (enabled) fetchPosts();
    }, [enabled, fetchPosts]);
    return {
        postManifest,
        isLoading,
        isFetched,
        isError,
    };
};

export const useHelpPost = ({ slug }: { slug?: string }) => {
    const [post, setPost] = useState<Post>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isError, setIsError] = useState<boolean>(false);
    const fetchPost = useCallback(async () => {
        setIsLoading(true);
        setIsError(false);
        try {
            const response = await fetch(`/admin/json-files/${slug}.json`);
            const post = await response.json();
            setPost({
                file: post.file,
                title: post.title,
                slug: post.slug,
                body: post.content,
                staffOnly: post.staffOnly,
                isPublished: post.isPublished,
            });
        } catch {
            setIsError(true);
        } finally {
            setIsLoading(false);
        }
    }, [slug]);

    useEffect(() => {
        if (slug) fetchPost();
        else setPost(undefined);
    }, [fetchPost, slug]);

    return {
        post,
        isLoading,
        isError,
    };
};

export const filterAndSortPosts = <
    P extends Pick<Post, "subject" | "staffOnly" | "ranking" | "isPublished">[],
>(
    posts: P,
    isUserStaff?: boolean,
    subjectToFilter?: Post["subject"],
): P => {
    return posts
        .filter(
            ({ isPublished, staffOnly, subject }) =>
                isPublished &&
                (isUserStaff || !staffOnly) &&
                ((subjectToFilter === "none" && !subject) ||
                    subject === subjectToFilter),
        )
        .toSorted(({ ranking: postARanking }, { ranking: postBRanking }) => {
            if (!postARanking) return 1;
            if (!postBRanking) return -1;
            return postARanking - postBRanking;
        }) as P;
};
